import { Currency, CurrencyAmount, NativeCurrencyName, Percent, TradeType } from '@kodiak-finance/sdk-core'
import { validateQuoteFromResponse, ValidationResult } from 'components/swap/decodeQuote'
import { chainIdToNativeCurrencyName, SupportedChainId } from 'config/chains'
import { ZERO_ADDRESS } from 'constants/misc'
import { DEFAULT_AUTO_SLIPPAGE } from 'hooks/useAutoSlippageTolerance'
import { useBestTrade } from 'hooks/useBestTrade'
import useBGTRedeemCallback, { BGTRedeemType } from 'hooks/useBGTRedeemCallback'
import { useConnection } from 'hooks/useConnection'
import { useIsSwapUnsupported } from 'hooks/useIsSwapUnsupported'
import { useResolveCurrencyAddress } from 'hooks/useResolveCurrencyAddress'
import useWrapCallback, { WrapType } from 'hooks/useWrapCallback'
import parseENSAddress from 'lib/utils/parseENSAddress'
import tryParseCurrencyAmount from 'lib/utils/tryParseCurrencyAmount'
import { ParsedQs } from 'qs'
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { Trade, TradeState } from 'state/routing/trades'
import { useUserSlippageToleranceWithDefault } from 'state/user/hooks'
import { setQueryParam, setQueryParams } from 'utils/queryParams'

import { REFETCH_POLICY_FAST } from '../../config/RefetchPolicies'
import { BGT_ADDRESS, PROMOTE_TOKENS_BY_CHAIN } from '../../constants/tokens'
import { useCurrency } from '../../hooks/Tokens'
import useParsedQueryString from '../../hooks/useParsedQueryString'
import { getParsedChainId, getTokenAddress } from '../../lib/utils/utils'
import { isAddress } from '../../utils'
import { useCurrencyBalances } from '../connection/hooks'
import { AppState } from '../index'
import {
  Field,
  replaceSwapState,
  selectCurrency,
  selectTrade,
  setRecipient,
  switchCurrencies,
  typeInput,
} from './actions'
import { SwapState } from './reducer'

export function useSwapState(): AppState['swap'] {
  return useAppSelector((state) => state.swap)
}

export function useSwapActionHandlers(): {
  onCurrencySelection: (field: Field, currency: Currency) => void
  onSwitchTokens: () => void
  onUserInput: (field: Field, typedValue: string) => void
  onChangeRecipient: (recipient: string | null) => void
  onSelectTrade: (tradeId: string) => void
} {
  const { chainId } = useConnection()
  const dispatch = useAppDispatch()
  const { INPUT, OUTPUT } = useSwapState()
  const onCurrencySelection = useCallback(
    (field: Field, currency: Currency) => {
      const nativeSymbol = chainIdToNativeCurrencyName[chainId]

      setQueryParam(
        `${field.toLowerCase()}Currency`,
        currency.isNative ? nativeSymbol : currency.wrapped.address.toLowerCase()
      )

      dispatch(
        selectCurrency({
          field,
          currencyId: currency.isToken ? currency.address : currency.isNative ? nativeSymbol : '',
        })
      )
    },
    [dispatch, chainId]
  )

  const onSwitchTokens = useCallback(() => {
    setQueryParams({
      inputCurrency: OUTPUT.currencyId as string,
      outputCurrency: INPUT.currencyId as string,
    })
    dispatch(switchCurrencies())
  }, [dispatch, INPUT, OUTPUT])

  const onUserInput = useCallback(
    (field: Field, typedValue: string) => {
      dispatch(typeInput({ field, typedValue }))
    },
    [dispatch]
  )

  const onChangeRecipient = useCallback(
    (recipient: string | null) => {
      dispatch(setRecipient({ recipient }))
    },
    [dispatch]
  )

  const onSelectTrade = useCallback(
    (tradeId: string) => {
      dispatch(selectTrade({ tradeId }))
    },
    [dispatch]
  )

  return {
    onSwitchTokens,
    onCurrencySelection,
    onUserInput,
    onChangeRecipient,
    onSelectTrade,
  }
}

const BAD_RECIPIENT_ADDRESSES: { [address: string]: true } = {
  // '0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f': true, // v2 factory
  // '0xf164fC0Ec4E93095b804a4795bBe1e041497b92a': true, // v2 router 01
  // '0x22704908fb549921beeab77aca4d2bf044e9958e': true, // v2 router 02
}

const isBGTToken = (currency: Currency | null | undefined): boolean => {
  if (!currency) return false

  return currency.isToken && currency.address?.toLowerCase() === BGT_ADDRESS.toLowerCase()
}

export function useDerivedSwapInfo(
  selectedProviders: string[],
  pauseFetch?: boolean
): {
  currencies: { [field in Field]?: Currency | null }
  currencyBalances: { [field in Field]?: CurrencyAmount<Currency> }
  parsedAmount: CurrencyAmount<Currency> | undefined
  inputError?: ReactNode
  selectedTrade: Trade | undefined
  tradeState: TradeState
  trades: Trade[]
  allowedSlippage: Percent
  isQuoteSyncing: boolean
  isBGTSwap: boolean
  isUnsupportedSwap: boolean
  refetch: () => void
  isRefetching: boolean
  calldataValidation: ValidationResult | undefined
} {
  const { account, chainId } = useConnection()

  const {
    independentField,
    typedValue,
    selectedTradeId,
    [Field.INPUT]: { currencyId: inputCurrencyId },
    [Field.OUTPUT]: { currencyId: outputCurrencyId },
    recipient,
  } = useSwapState()

  const inputCurrency = useCurrency(inputCurrencyId)
  const outputCurrency = useCurrency(outputCurrencyId)

  const currencies = useMemo(
    () => ({
      [Field.INPUT]: inputCurrency,
      [Field.OUTPUT]: outputCurrency,
    }),
    [inputCurrency, outputCurrency]
  )

  const isBGTSwap = isBGTToken(inputCurrency) || isBGTToken(outputCurrency)
  const isUnsupportedSwap = useIsSwapUnsupported(inputCurrency, outputCurrency)

  const resolvedRecipient = useMemo(() => {
    if (recipient === null) return account
    return isAddress(recipient) ? recipient : null
  }, [recipient, account])

  const { wrapType } = useWrapCallback(inputCurrency, outputCurrency, typedValue)
  const { redeemType } = useBGTRedeemCallback(inputCurrency, outputCurrency, typedValue)

  const shouldPauseAPIFetch =
    wrapType !== WrapType.NOT_APPLICABLE ||
    redeemType !== BGTRedeemType.NOT_APPLICABLE ||
    isBGTSwap ||
    isUnsupportedSwap ||
    pauseFetch

  const isExactIn = independentField === Field.INPUT

  const parsedAmount = useMemo(
    () => tryParseCurrencyAmount(typedValue, (isExactIn ? inputCurrency : outputCurrency) ?? undefined),
    [typedValue, isExactIn, inputCurrency, outputCurrency]
  )

  const {
    bestTrade,
    state: tradeState,
    trades,
    isQuoteSyncing,
    refetch,
    isRefetching,
  } = useBestTrade(
    isExactIn ? TradeType.EXACT_INPUT : TradeType.EXACT_OUTPUT,
    selectedProviders,
    parsedAmount,
    (isExactIn ? outputCurrency : inputCurrency) ?? undefined,
    shouldPauseAPIFetch,
    resolvedRecipient
  )

  const selectedTrade = useMemo(() => {
    if (selectedTradeId) {
      const userSelection = trades.find((trade) => trade.id === selectedTradeId)
      if (userSelection) return userSelection
    }

    return bestTrade
  }, [bestTrade, selectedTradeId, trades])

  const currencyBalances = useCurrencyBalances(
    account ?? undefined,
    [inputCurrency ?? undefined, outputCurrency ?? undefined],
    REFETCH_POLICY_FAST
  )

  const balances = useMemo(
    () => ({
      [Field.INPUT]: currencyBalances[0],
      [Field.OUTPUT]: currencyBalances[1],
    }),
    [currencyBalances]
  )

  const allowedSlippage = useUserSlippageToleranceWithDefault(DEFAULT_AUTO_SLIPPAGE)

  const inputError = useMemo(() => {
    if (!account) return <>Connect Wallet</>
    if (!inputCurrency || !outputCurrency) return <>Select a token</>
    if (!parsedAmount) return <>Enter an amount</>

    if (!resolvedRecipient) return <>Enter a recipient</>
    if (BAD_RECIPIENT_ADDRESSES[resolvedRecipient]) return <>Invalid recipient</>

    const inputBalance = balances[Field.INPUT]
    const requiredAmount = selectedTrade?.maximumAmountIn?.(allowedSlippage)

    if (inputBalance && requiredAmount && inputBalance.lessThan(requiredAmount)) {
      return <>Insufficient {requiredAmount.currency.symbol} balance</>
    }

    return undefined
  }, [
    account,
    inputCurrency,
    outputCurrency,
    parsedAmount,
    resolvedRecipient,
    balances,
    selectedTrade,
    allowedSlippage,
  ])

  const calldataValidation = useMemo(() => {
    if (
      !selectedTrade?.methodParameters?.calldata ||
      !account ||
      !parsedAmount ||
      !inputCurrency ||
      !outputCurrency ||
      isQuoteSyncing ||
      isRefetching ||
      tradeState !== TradeState.VALID
    ) {
      return undefined
    }

    if (selectedTrade.inputAmount.quotient.toString() !== parsedAmount.quotient.toString()) {
      return undefined
    }

    if (
      getTokenAddress(selectedTrade.inputAmount.currency) !== getTokenAddress(inputCurrency) ||
      getTokenAddress(selectedTrade.outputAmount.currency) !== getTokenAddress(outputCurrency)
    ) {
      return undefined
    }

    try {
      return validateQuoteFromResponse(
        {
          methodParameters: selectedTrade.methodParameters,
          quoteDecimals: selectedTrade.outputAmount.quotient.toString(),
        },
        {
          chainId,
          recipient: resolvedRecipient || account,
          tokenInAddress: getTokenAddress(inputCurrency),
          tokenOutAddress: getTokenAddress(outputCurrency),
          amountIn: parsedAmount.quotient.toString(),
          slippageTolerance: parseFloat(allowedSlippage.toFixed(2)),
        }
      )
    } catch (error) {
      console.warn('Calldata validation failed:', error)
      return undefined
    }
  }, [
    selectedTrade,
    account,
    parsedAmount,
    inputCurrency,
    outputCurrency,
    isQuoteSyncing,
    isRefetching,
    tradeState,
    resolvedRecipient,
    allowedSlippage,
    chainId,
  ])

  return {
    currencies,
    currencyBalances: balances,
    parsedAmount,
    inputError,
    selectedTrade,
    tradeState,
    trades,
    allowedSlippage,
    isQuoteSyncing,
    isBGTSwap,
    isUnsupportedSwap,
    refetch,
    isRefetching,
    calldataValidation,
  }
}

const parseCurrencyFromURLParameter = (urlParam: ParsedQs[string], chainId: number): string => {
  if (typeof urlParam !== 'string') return ''

  const upper = urlParam.toUpperCase()
  const nativeSymbol = chainIdToNativeCurrencyName[chainId as SupportedChainId]

  if (urlParam === ZERO_ADDRESS) return nativeSymbol

  if (upper === nativeSymbol || upper === 'ETH') return nativeSymbol

  const validAddress = isAddress(urlParam)
  if (validAddress) return validAddress
  return upper
}

function parseTokenAmountURLParameter(urlParam: any): string {
  return typeof urlParam === 'string' && !isNaN(parseFloat(urlParam)) ? urlParam : ''
}

const ADDRESS_REGEX = /^0x[a-fA-F0-9]{40}$/
function validatedRecipient(recipient: any): string | null {
  if (typeof recipient !== 'string') return null
  const address = isAddress(recipient)
  if (address) return address
  if (parseENSAddress(recipient)) return recipient
  if (ADDRESS_REGEX.test(recipient)) return recipient
  return null
}

export function queryParametersToSwapState(parsedQs: ParsedQs, chainId: SupportedChainId): SwapState {
  let inputCurrency = parseCurrencyFromURLParameter(parsedQs.inputCurrency, chainId)
  let outputCurrency = parseCurrencyFromURLParameter(parsedQs.outputCurrency, chainId)
  // Exact-output swaps are not exposed yet: an output amount from a link can't be reused as the input amount
  const isExactOutputLink = typeof parsedQs.exactField === 'string' && parsedQs.exactField.toLowerCase() === 'output'
  const typedValue = isExactOutputLink ? '' : parseTokenAmountURLParameter(parsedQs.exactAmount)
  const independentField = Field.INPUT

  if (inputCurrency === '' && outputCurrency === '' && typedValue === '') {
    // Defaults to having the native currency selected
    inputCurrency = getDefaultSwapCurrencyIds(chainId).input ?? NativeCurrencyName.BERA
  } else if (inputCurrency === outputCurrency) {
    // clear output if identical
    outputCurrency = ''
  }

  const recipient = validatedRecipient(parsedQs.recipient)

  return {
    [Field.INPUT]: {
      currencyId: inputCurrency === '' ? null : inputCurrency ?? null,
    },
    [Field.OUTPUT]: {
      currencyId: outputCurrency === '' ? null : outputCurrency ?? null,
    },
    typedValue,
    independentField,
    recipient,
    selectedTradeId: null,
  }
}

function getDefaultSwapCurrencyIds(chainId: SupportedChainId): { input?: string; output?: string } {
  return {
    input: chainIdToNativeCurrencyName[chainId],
    output: PROMOTE_TOKENS_BY_CHAIN[chainId]?.address,
  }
}

let swapStateChainId: SupportedChainId | undefined

// updates the swap state to use the defaults for a given network
export function useDefaultsFromURLSearch(): SwapState & { canSyncUrl: boolean } {
  const { chainId } = useConnection()
  const dispatch = useAppDispatch()
  const parsedQs = useParsedQueryString()
  const [isInitialized, setIsInitialized] = useState(false)
  const initializedChainId = useRef<SupportedChainId>()

  const urlChainId = getParsedChainId(parsedQs)
  const isChainSynced = urlChainId === undefined || urlChainId === chainId

  const parsedSwapState = useMemo(() => {
    return queryParametersToSwapState(parsedQs, chainId)
  }, [parsedQs, chainId])

  const promoteToken = useMemo(() => {
    return PROMOTE_TOKENS_BY_CHAIN[chainId as SupportedChainId]
  }, [chainId])

  const rawInputCurrencyId = parsedSwapState[Field.INPUT].currencyId
  const rawOutputCurrencyId = parsedSwapState[Field.OUTPUT].currencyId

  const resolvedInputId = useResolveCurrencyAddress(rawInputCurrencyId)
  const resolvedOutputId = useResolveCurrencyAddress(rawOutputCurrencyId)

  const finalOutputId = useMemo(() => {
    if (resolvedInputId && resolvedOutputId && resolvedInputId === resolvedOutputId) {
      return undefined
    }

    const canOutputBePromoted = resolvedInputId !== promoteToken?.address
    return resolvedOutputId ?? (canOutputBePromoted ? promoteToken?.address : undefined)
  }, [resolvedInputId, resolvedOutputId, promoteToken])

  // The first effect initialize swap state from URL parameters (runs once on mount)
  useEffect(() => {
    if (!chainId || initializedChainId.current !== undefined || !isChainSynced) return

    initializedChainId.current = chainId
    swapStateChainId = chainId

    dispatch(
      replaceSwapState({
        typedValue: parsedSwapState.typedValue,
        field: parsedSwapState.independentField,
        inputCurrencyId: parsedSwapState[Field.INPUT].currencyId ?? undefined,
        outputCurrencyId: parsedSwapState[Field.OUTPUT].currencyId ?? finalOutputId,
        recipient: parsedSwapState.recipient,
      })
    )
    setIsInitialized(true)
  }, [chainId, dispatch, parsedSwapState, isChainSynced, finalOutputId])

  useEffect(() => {
    const previousChainId = initializedChainId.current
    if (!chainId || previousChainId === undefined || !isChainSynced || previousChainId === chainId) return

    initializedChainId.current = chainId
    swapStateChainId = chainId
    const defaults = getDefaultSwapCurrencyIds(chainId)

    setQueryParams({
      inputCurrency: defaults.input ?? null,
      outputCurrency: defaults.output ?? null,
      exactAmount: null,
      exactField: null,
    })
    dispatch(
      replaceSwapState({
        typedValue: '',
        field: Field.INPUT,
        inputCurrencyId: defaults.input,
        outputCurrencyId: defaults.output,
        recipient: parsedSwapState.recipient,
      })
    )
  }, [chainId, dispatch, isChainSynced, parsedSwapState.recipient])

  // The second Effect update resolved input currency when it changes (after initialization)
  useEffect(() => {
    if (!chainId || initializedChainId.current === undefined || !resolvedInputId) return
    dispatch(selectCurrency({ field: Field.INPUT, currencyId: resolvedInputId }))
  }, [dispatch, chainId, resolvedInputId])

  // And the third Effect update resolved output currency when it changes (after initialization)
  useEffect(() => {
    if (!chainId || initializedChainId.current === undefined || !finalOutputId) return
    dispatch(selectCurrency({ field: Field.OUTPUT, currencyId: finalOutputId }))
  }, [dispatch, chainId, finalOutputId])

  const canSyncUrl = isInitialized || swapStateChainId === chainId

  return useMemo(() => ({ ...parsedSwapState, canSyncUrl }), [parsedSwapState, canSyncUrl])
}
